import React, { useState, useEffect } from "react";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import dayjs from "dayjs";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";

import { useAuth } from "../../hooks/AuthProvider";
import { GET_RCS_TEMPLATES } from "../../config/urls";
import { formatWithTzLabel } from "../../utils/dateUtils";

import TableSearchInput from "../TableSearchInput";
import TableFilterButton from "../TableFilterButton";
import { FilterModal, FilterModalItem } from "../FilterModal";
import { Category, Style } from "@mui/icons-material";
import AppTable from "../../common/app-table";
import AppDropdown from "../../common/app-dropdown";
import ButtonTwo from "../../common/dashboard-button";
import AppChip from "../../common/app-chip";
import { ColorsConstants } from "../../cssUtils/constants";

export default function RCSTemplateAnalyticsTable({
  bot, // Still accept it just in case it gets passed later
  refreshKey,
  onTemplateClick,
}) {
  const { getValidToken } = useAuth();
  
  const [rowCount, setRowCount] = useState(0);
  const [templatesData, setTemplateData] = useState([]);
  
  // Pagination & Filtering State
  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: 20,
    serverSidePage: 0,
  });
  
  const [searchQuery, setSearchQuery] = useState("");
  const [charCount, setCharCount] = useState(0);
  const [useCaseFilter, setUseCaseFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  const capitalizeFirstLetter = (str) => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase().replace(/_/g, " ");
  };

  const fetchData = async () => {
    const params = {
      limit: controller.rowsPerPage.toString(),
      offset: (controller.serverSidePage * controller.rowsPerPage).toString(),
      status: "APPROVED" // Hardcoded to only fetch ready-to-use templates
    };

    if (searchQuery) params.search = searchQuery;
    if (useCaseFilter) params.template_use_case = useCaseFilter;
    if (typeFilter) params.template_type = typeFilter;
    
    // 🟢 Only append bot_id if it actually exists, otherwise leave it out
    if (bot?.id) params.bot_id = bot.id; 

    const queryParams = new URLSearchParams(params);

    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const response = await fetch(
        `${GET_RCS_TEMPLATES}?${queryParams}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-Tenant-Id": tenant,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch RCS templates");
      
      const jsonData = await response.json();
      setTemplateData(jsonData.results || []);
      setRowCount(jsonData.count || 0);
    } catch (error) {
      console.error("Error fetching RCS templates:", error.message);
    }
  };

  useEffect(() => {
    // 🟢 FIXED: Removed the `if (bot?.id)` check. 
    // Now it fetches all templates immediately when the component mounts!
    fetchData();
  }, [
    controller.serverSidePage,
    controller.rowsPerPage,
    refreshKey,
    searchQuery,
    useCaseFilter,
    typeFilter,
    bot?.id // Keep as a dependency so if a bot IS selected later, it refetches
  ]);

  // Handlers
  const handlePageChange = (event, newPage) => {
    setController((prev) => ({
      ...prev,
      page: newPage,
      serverSidePage: newPage,
    }));
  };

  const handleChangeRowsPerPage = (event) => {
    setController({
      ...controller,
      rowsPerPage: parseInt(event.target.value, 10),
      page: 0,
      serverSidePage: 0,
    });
  };

  const handleSearchQueryChange = (event) => {
    if (event.target.value.length > 50) return;
    setSearchQuery(event.target.value);
    setCharCount(event.target.value.length);
    setController((prev) => ({ ...prev, page: 0, serverSidePage: 0 }));
  };

  const handleTemplateNameClick = (template, e) => {
    e.preventDefault();
    if (onTemplateClick) {
      onTemplateClick(template);
    }
  };

  const resetAllFilters = () => {
    setSearchQuery("");
    setUseCaseFilter("");
    setTypeFilter("");
    setController((prev) => ({ ...prev, page: 0, serverSidePage: 0 }));
  };

  const getTotalActiveFilters = () => {
    let count = 0;
    if (useCaseFilter) count++;
    if (typeFilter) count++;
    return count;
  };

  // Static options based on your Django Model choices
  const useCaseOptions = [
    { label: "Promotional", value: "Promotional" },
    { label: "Transactional", value: "Transactional" },
    { label: "OTP", value: "OTP" },
  ];

  const typeOptions = [
    { label: "Text Message", value: "text_message" },
    { label: "Text + Media", value: "text_message_with_media" },
    { label: "Text + PDF", value: "text_message_with_pdf" },
    { label: "Rich Card", value: "rich_card" },
    { label: "Carousel", value: "carousel" },
  ];

  return (
    <div>
      {/* Mobile Header */}
      <Box sx={{ mb: 2, display: { xs: "flex", md: "none" }, flexDirection: "column", gap: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
          <TableSearchInput
            searchQuery={searchQuery}
            onSearchChange={handleSearchQueryChange}
            charCount={charCount}
            tooltip="Search by Template Name"
          />
          <TableFilterButton
            setFilterModalOpen={setFilterModalOpen}
            totalActiveFilters={getTotalActiveFilters()}
          />
        </Box>
      </Box>

      <Paper sx={{ width: "100%", overflow: "hidden", mt: 2, boxShadow: "none", bgcolor: "transparent" }}>
        
        {/* Desktop Header */}
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "flex-end",
            flexDirection: "column",
            p: 1,
            m: 1,
            bgcolor: "background.paper",
            borderRadius: 1,
          }}
        >
          <Grid container spacing={1}>
            <Grid item xs={3} md={3} mb={2}>
              <TableSearchInput
                searchQuery={searchQuery}
                onSearchChange={handleSearchQueryChange}
                charCount={charCount}
                tooltip="Search by Template Name or Content"
              />
            </Grid>

            <Grid item xs={2.5} md={2.5}>
              <AppDropdown
                endIcon={<KeyboardArrowDownOutlinedIcon />}
                menuItems={useCaseOptions}
                selectedValue={useCaseFilter}
                onMenuSelect={(value) => setUseCaseFilter(value)}
                sx={{ width: "100%" }}
              >
                {useCaseFilter ? capitalizeFirstLetter(useCaseFilter) : "Use Case"}
              </AppDropdown>
            </Grid>

            <Grid item xs={2.5}>
              <AppDropdown
                endIcon={<KeyboardArrowDownOutlinedIcon />}
                menuItems={typeOptions}
                selectedValue={typeFilter}
                onMenuSelect={(value) => setTypeFilter(value)}
                sx={{ width: "100%" }}
              >
                {typeFilter ? capitalizeFirstLetter(typeFilter) : "Template Type"}
              </AppDropdown>
            </Grid>

            <Grid item>
              <ButtonTwo
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={resetAllFilters}
                sx={{ height: "40px", borderRadius: "8px", textTransform: "capitalize", border: `1px solid ${ColorsConstants.primaryBackgroundColor}`, color: ColorsConstants.primaryBackgroundColor }}
              >
                Reset
              </ButtonTwo>
            </Grid>
          </Grid>
        </Box>

        {/* Table Area */}
        <AppTable
          columns={[
            {
              id: "name",
              label: "Template Name",
              minWidth: 180,
              render: (template) => (
                <a
                  href="#"
                  style={{ color: ColorsConstants.primaryBlue100, fontWeight: 500, textDecoration: "none" }}
                  onClick={(e) => handleTemplateNameClick(template, e)}
                >
                  {template.name}
                </a>
              ),
            },
            {
              id: "template_use_case",
              label: "Use Case",
              minWidth: 120,
              render: (t) => capitalizeFirstLetter(t.template_use_case) || "Unknown",
            },
            {
              id: "template_type",
              label: "Type",
              minWidth: 140,
              render: (t) => capitalizeFirstLetter(t.template_type),
            },
            {
              id: "created_at",
              label: "Created Date",
              minWidth: 130,
              render: (t) => dayjs(t.created_at).format("DD/MM/YYYY"),
            },
            {
              id: "last_broadcast_datetime",
              label: "Last Broadcast",
              minWidth: 160,
              render: (t) => t.last_broadcast_datetime_iso ? formatWithTzLabel(t.last_broadcast_datetime_iso, "DD/MM/YYYY HH:mm") : "Never",
            },
          ]}
          rows={templatesData}
          rowKey={(t) => t.id}
          emptyMessage="No RCS templates found matching criteria"
          maxHeight={530}
          count={rowCount}
          page={controller.page}
          rowsPerPage={controller.rowsPerPage}
          rowsPerPageOptions={[20, 50, 100]}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Mobile Filter Modal */}
      <FilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        onReset={resetAllFilters}
      >
        <FilterModalItem
          name="Use Case"
          data={useCaseOptions.map(o => o.value)}
          value={useCaseFilter}
          onChange={(e) => setUseCaseFilter(e.target.value)}
          icon={<Category />}
        />
        <FilterModalItem
          name="Template Type"
          data={typeOptions.map(o => o.value)}
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          icon={<Style />}
        />
      </FilterModal>
    </div>
  );
}